/**
 * Link Prefetch
 *
 * Preloads internal links on hover/touch for instant navigation.
 * Uses Quicklink library for intelligent prefetching with built-in safeguards.
 *
 * Features:
 * - Hover-based prefetching (desktop)
 * - Touch-based prefetching (mobile)
 * - Intersection Observer for viewport-based prefetching
 * - Respects data saver mode
 * - Throttles concurrent requests
 * - Same-origin only
 */

import { listen, prefetch } from 'quicklink';

export async function init() {
  // Track already prefetched URLs to avoid duplicates
  const prefetched = new Set<string>();

  // Start listening for viewport-based prefetching
  const cleanup = listen({
    // Listen on entire document
    el: document.body,

    // Limit concurrent prefetches (prevents bandwidth overload)
    limit: 5,

    // Throttle network requests (spacing between prefetches in ms)
    throttle: 1000,

    // Timeout for each prefetch request (ms)
    timeout: 10000,

    // Respect user's data saver preference
    ignoreSlowConnection: true,

    // Only prefetch links with these origins (defaults to same-origin)
    origins: [window.location.hostname],

    // Delay before prefetch starts (ms)
    delay: 0,

    // Priority hints for fetch
    priority: 'low',
  });

  // Add IntersectionObserver to prefetch links when they enter viewport
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const link = entry.target as HTMLAnchorElement;
          const href = link.href;

          // Skip hash links and already prefetched
          if (href.includes('#') || prefetched.has(href)) {
            return;
          }

          // Prefetch when entering viewport
          prefetch([href], { priority: 'low' })
            .then(() => {
              prefetched.add(href);
            })
            .catch(() => {
              // Silent error handling
            });
        }
      });
    },
    { rootMargin: '200px' } // Start prefetching 200px before entering viewport
  );

  // Observe all same-origin links
  document.querySelectorAll('a[href]').forEach((link) => {
    if (link instanceof HTMLAnchorElement && link.hostname === window.location.hostname) {
      observer.observe(link);
    }
  });

  // Add manual hover listeners with explicit prefetching
  document.body.addEventListener('mouseover', (e) => {
    const link = (e.target as Element).closest('a[href]');
    if (link && link instanceof HTMLAnchorElement) {
      const href = link.href;
      const isSameOrigin = link.hostname === window.location.hostname;

      // Manually trigger prefetch on hover
      if (isSameOrigin && !prefetched.has(href)) {
        prefetch([href], { priority: 'low' })
          .then(() => {
            prefetched.add(href);
          })
          .catch(() => {
            // Silent error handling
          });
      }
    }
  });

  return {
    result: 'link-prefetch initialized',
    destroy: () => {
      if (cleanup && typeof cleanup === 'function') {
        cleanup();
      }
      observer.disconnect();
    },
  };
}
